import { useState } from 'react'
import { PrepChips, PrepLine } from './TaskPrep'
import { objectiveText } from './tarkovData'
import { prerequisiteNames } from './taskGraph'
import type { Task, TaskObjective } from './types'
import { useI18n } from './i18n'

type Props = {
  /** 表示中のマップに関係する登録タスクIDを、表示順に並べたもの */
  order: string[]
  /** 全マップ合計の登録件数。絞り込みで空になったときの案内に使う */
  registeredCount: number
  /** 表示中のマップの画面名。'' なら絞り込んでいない */
  mapName: string
  /** normalizedName → 画面に出す名前 */
  mapLabels: Record<string, string>
  byId: Map<string, Task>
  /** カッパコンテナに要るタスク（前提チェーン込み）。行に `Κ` を出す */
  kappaIds: Set<string>
  /** taskId → 登録した人。label は入力された名前、空ならスロットID（A/B/C）。誰が登録したか表示するため */
  ownersOf: (taskId: string) => { id: string; label: string }[]
  /** 非表示にされているタスクID。行を薄くし、マップから図形を消す（全員で共有） */
  hiddenIds: Set<string>
  /** 表示中のマップに図形を持つタスクID。非表示ボタンはこれに入る行にだけ出す */
  markableIds: Set<string>
  onToggleHidden: (taskId: string) => void
  onMove: (taskId: string, dir: -1 | 1) => void
  /** つかんだ行を、落とした行の位置へ入れる */
  onDrop: (taskId: string, targetId: string) => void
  /** タスク名の昇順に並べ替える（全員に反映される） */
  onSortByName: () => void
  onRemove: (taskId: string) => void
}

/**
 * 目標を画面に出す形にする。**文がまったく同じものは1行にまとめて件数を添える。**
 *
 * 上流の日本語訳が品名を落とすせいで、別々の目標が同じ文になることがある
 * （`Samples` の「注射器 を渡す」は7件）。個数を足しても見分けが付かないものだけが残る。
 * → doc/task-board/master.md#目標の表示
 */
function objectiveLines(objectives: TaskObjective[]) {
  const out: { key: string; text: string; optional: boolean; n: number }[] = []
  for (const o of objectives) {
    const text = objectiveText(o)
    const hit = out.find((x) => x.text === text && x.optional === o.optional)
    if (hit) hit.n += 1
    else out.push({ key: o.id || text, text, optional: o.optional, n: 1 })
  }
  return out
}

/**
 * 登録タスクの一覧。表示中のマップのぶんだけを重複なしで並べ、
 * この並びのとおりに 1 から番号を振る（マップ上の図形の番号と同じもの）。
 *
 * 行は番号の左のボタンで畳める。畳むと **タスク名と準備物マークだけの1行**になり、
 * 何件も登録したときに一覧を見渡せる。開閉は行ごとのローカル状態（共有しない）。
 */
export function TaskList({
  order,
  registeredCount,
  mapName,
  mapLabels,
  byId,
  kappaIds,
  ownersOf,
  hiddenIds,
  markableIds,
  onToggleHidden,
  onMove,
  onDrop,
  onSortByName,
  onRemove,
}: Props) {
  // このファイルの `t` はタスク。訳語は `msg` で引く
  const { t: msg } = useI18n()
  // ドラッグ中の行と、今どの行の上にいるか。並び自体は確定するまで動かさない
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  /** 畳んでいる行。タスク名と準備物マークだけにする */
  const [folded, setFolded] = useState<Set<string>>(new Set())

  const toggleFold = (id: string) =>
    setFolded((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  const items = order.map((id) => byId.get(id)).filter((t): t is Task => !!t)

  if (items.length === 0) {
    return (
      <div className="tasklist tasklist--empty">
        {registeredCount > 0 ? (
          <>
            {msg('tasklist.emptyForMap', { n: registeredCount })}
            <br />
            {msg('tasklist.emptyForMap.hint')}
          </>
        ) : (
          <>{msg('tasklist.empty')}</>
        )}
      </div>
    )
  }

  const endDrag = () => {
    setDragId(null)
    setOverId(null)
  }

  return (
    <>
      {/* 並べ替えは共有データ（`order`）を書き換える。番号も振り直る */}
      <div className="tasklist__bar">
        <button
          type="button"
          className="tasklist__sort"
          onClick={onSortByName}
          title={msg('tasklist.sort.title')}
        >
          {msg('tasklist.sort')}
        </button>
      </div>
      <ol className="tasklist">
        {items.map((t, i) => {
          const fold = folded.has(t.id)
          const prereq = prerequisiteNames(t, byId)
          return (
          <li
            key={t.id}
            className={`tl${dragId === t.id ? ' tl--dragging' : ''}${
              overId === t.id && dragId && dragId !== t.id ? ' tl--over' : ''
            }${hiddenIds.has(t.id) ? ' tl--hidden' : ''}${fold ? ' tl--folded' : ''}`}
            draggable
            onDragStart={(e) => {
              // リンクやボタンからのドラッグは本来の動き（リンクのドラッグ）に任せる
              if ((e.target as HTMLElement).closest('a, button, input, textarea')) {
                e.preventDefault()
                return
              }
              setDragId(t.id)
              e.dataTransfer.effectAllowed = 'move'
              e.dataTransfer.setData('text/plain', t.id)
            }}
            onDragOver={(e) => {
              if (!dragId || dragId === t.id) return
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
              setOverId(t.id)
            }}
            onDrop={(e) => {
              if (!dragId || dragId === t.id) return
              e.preventDefault()
              onDrop(dragId, t.id)
              endDrag()
            }}
            onDragEnd={endDrag}
          >
            {/* 番号の左。押すとこの行だけ畳む／開く */}
            <button
              type="button"
              className="tl__fold"
              onClick={() => toggleFold(t.id)}
              aria-expanded={!fold}
              title={fold ? msg('tasklist.fold.open.title') : msg('tasklist.fold.close.title')}
              aria-label={fold ? msg('tasklist.fold.open') : msg('tasklist.fold.close')}
            >
              {fold ? '▸' : '▾'}
            </button>

            <span className="tl__no" title={msg('tasklist.no.title', { n: i + 1 })}>
              {i + 1}
            </span>

            <div className="tl__body">
              <div className="tl__head">
                <a
                  className="tl__name"
                  href={t.wikiLink}
                  target="_blank"
                  rel="noreferrer"
                  draggable={false}
                  title={msg('tasklist.wiki')}
                >
                  {t.name}
                </a>
                {/* Collector とその前提チェーン。実データで 517件中13件（→ src/taskGraph.ts） */}
                {kappaIds.has(t.id) && (
                  <span className="tl__kappa" title={msg('task.kappa.title')}>
                    {msg('task.kappa')}
                  </span>
                )}
                {/* 畳んだ行は「タスク名 + 準備物マーク」だけ。詳細は開いたときに出す */}
                {fold && (
                  <span className="tl__chips">
                    <PrepChips prep={t.prep} />
                  </span>
                )}

                {!fold && (
                <span className="tl__meta">
                  {t.trader}
                  {t.maps.length > 0
                    ? ` · ${t.maps.map((m) => mapLabels[m] ?? m).join(' / ')}`
                    : mapName && ` · ${msg('tasklist.anyMap')}`}
                  {/* 座標を持つタスクは 517件中178件だけ。残りは地図に描けないので、
                      「登録したのに何も出ない」と見えないよう一覧の側で断っておく */}
                  {!t.hasZones && (
                    <span className="tl__nozone" title={msg('tasklist.nozone.title')}>
                      {msg('tasklist.nozone')}
                    </span>
                  )}
                </span>
                )}
                {!fold && (
                <span className="tl__owners">
                  {ownersOf(t.id).map((o) => (
                    <span key={o.id} className="tl__owner" title={msg('tasklist.slot', { id: o.id })}>
                      {o.label}
                    </span>
                  ))}
                </span>
                )}
              </div>

              {/* 先に終わらせないと受けられないタスク。持っているのは 517件中221件 */}
              {!fold && prereq.length > 0 && (
                <div className="tl__needs" title={msg('tasklist.needs.title')}>
                  {msg('tasklist.needs', { names: prereq.join(' / ') })}
                </div>
              )}

              {/* 持ち込みが要るもの。畳んだ状態では種別と件数だけ → doc/task-prep/master.md */}
              {!fold && <PrepLine prep={t.prep} />}

              {!fold && t.objectives.length > 0 && (
                <ul className="tl__objectives">
                  {objectiveLines(t.objectives).map((o) => (
                    <li key={o.key} className={o.optional ? 'tl__objective--optional' : undefined}>
                      {o.text}
                      {o.n > 1 && (
                        <span className="tl__dupes" title={msg('tasklist.sameObjective.title')}>
                          {msg('tasklist.sameObjective', { n: o.n })}
                        </span>
                      )}
                      {o.optional && (
                        <span className="tl__optional" title={msg('tasklist.optional.title')}>
                          {msg('tasklist.optional')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 上から × / 非表示 / ↑ / ↓。行が何行になっても右上に貼り付く */}
            <div className="tl__actions">
              <button
                type="button"
                className="tl__remove"
                onClick={() => onRemove(t.id)}
                aria-label={msg('tasklist.remove')}
              >
                ×
              </button>

              {/* 図形を持つタスクにだけ出す。持たない行に出しても押した結果が見えない */}
              {markableIds.has(t.id) && (
                <button
                  type="button"
                  className={`tl__hide${hiddenIds.has(t.id) ? ' tl__hide--on' : ''}`}
                  onClick={() => onToggleHidden(t.id)}
                  aria-pressed={hiddenIds.has(t.id)}
                  aria-label={hiddenIds.has(t.id) ? msg('tasklist.show') : msg('tasklist.hide')}
                  title={hiddenIds.has(t.id) ? msg('tasklist.show.title') : msg('tasklist.hide.title')}
                >
                  {hiddenIds.has(t.id) ? '🚫' : '👁'}
                </button>
              )}

              <button
                type="button"
                onClick={() => onMove(t.id, -1)}
                disabled={i === 0}
                aria-label={msg('tasklist.up')}
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => onMove(t.id, 1)}
                disabled={i === items.length - 1}
                aria-label={msg('tasklist.down')}
              >
                ↓
              </button>
            </div>
          </li>
          )
        })}
      </ol>
    </>
  )
}
